import { io, Socket } from 'socket.io-client';

// Singleton pour la connexion Socket.IO
class SocketService {
  private static instance: SocketService;
  private socket: Socket | null = null;
  private listeners: Map<string, Set<Function>> = new Map();
  private reconnectTimer: NodeJS.Timeout | null = null;
  private connectionAttempts: number = 0;
  private maxReconnectAttempts: number = 10;

  private constructor() {
    // Constructeur privé pour le pattern Singleton
  }

  public static getInstance(): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService();
    }
    return SocketService.instance;
  }

  /**
   * Gérer la reconnexion manuelle
   */
  private handleReconnect(): void {
    this.connectionAttempts++;
    console.log(`Tentative de reconnexion ${this.connectionAttempts}/${this.maxReconnectAttempts}`);
    
    if (this.connectionAttempts <= this.maxReconnectAttempts) {
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
      }
      
      this.reconnectTimer = setTimeout(() => {
        console.log('Tentative de reconnexion...');
        if (this.socket) {
          this.socket.connect();
        } else {
          this.connect();
        }
      }, 3000); // Attendre 3 secondes avant de réessayer
    } else {
      console.error('Nombre maximum de tentatives de reconnexion atteint');
    }
  }

  /**
   * Initialiser la connexion Socket.IO
   */
  public connect(): Socket | null {
    if (this.socket) {
      return this.socket;
    }

    // Récupérer l'URL du socket depuis les variables d'environnement
    const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001';
    console.log('Socket URL utilisée:', SOCKET_URL);
    
    // Vérifier si nous sommes en environnement de production
    const isProduction = process.env.NODE_ENV === 'production';
    console.log('Environnement:', isProduction ? 'production' : 'développement');
    
    try {
      // Configurer les options de connexion
      this.socket = io(SOCKET_URL, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 20000,
        forceNew: true
      });

      // Gérer les événements de connexion
      this.socket.on('connect', () => {
        console.log('Connecté au serveur Socket.IO avec ID:', this.socket?.id);
        this.connectionAttempts = 0;
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
      });
      
      this.socket.on('connect_error', (error) => {
        console.error('Erreur de connexion Socket.IO:', error.message);
        this.handleReconnect();
      });
      
      this.socket.on('disconnect', (reason) => {
        console.log('Déconnecté du serveur Socket.IO. Raison:', reason);
        if (reason === 'io server disconnect') {
          // La déconnexion est initiée par le serveur, il faut se reconnecter manuellement
          this.handleReconnect();
        }
      });

      // Écouter les événements de mise à jour de statut SMS
      this.socket.on('sms:status-updated', (data) => {
        console.log('Événement de mise à jour de statut SMS reçu:', data);
        this.notifyListeners('sms:status-updated', data);
      });

      // Écouter les événements d'envoi de SMS
      this.socket.on('sms:sent', (data) => {
        console.log('Événement SMS envoyé reçu:', data);
        this.notifyListeners('sms:sent', data);
      });

      return this.socket;
    } catch (error) {
      console.error('Erreur lors de la connexion Socket.IO:', error);
      this.handleReconnect();
      return null;
    }
  }

  /**
   * Ajouter un écouteur pour un événement
   * @param event Nom de l'événement
   * @param callback Fonction à appeler lorsque l'événement est reçu
   */
  public addListener(event: string, callback: Function): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)?.add(callback);
  }

  /**
   * Supprimer un écouteur pour un événement
   * @param event Nom de l'événement
   * @param callback Fonction à supprimer
   */
  public removeListener(event: string, callback: Function): void {
    if (this.listeners.has(event)) {
      this.listeners.get(event)?.delete(callback);
    }
  }

  /**
   * Notifier tous les écouteurs d'un événement
   * @param event Nom de l'événement
   * @param data Données de l'événement
   */
  private notifyListeners(event: string, data: any): void {
    if (this.listeners.has(event)) {
      this.listeners.get(event)?.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Erreur lors de l'exécution d'un écouteur pour l'événement ${event}:`, error);
        }
      });
    }
  }

  /**
   * Fermer la connexion Socket.IO
   */
  public disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Obtenir la socket
   */
  public getSocket(): Socket | null {
    return this.socket;
  }
}

export default SocketService.getInstance();
